/* eslint-disable react-hooks/exhaustive-deps */

import { CalculationStrategy, PropertyType, Status } from '@/api/enumerations';
import manualCalcultionAPI from '@/api/manualCalculation';
import { CompletionData } from '@/api/manualCalculation/types';
import { getReportGraphs } from '@/api/workOrders';
import {
  AvmFactorsData,
  AvmInferenceData,
  GraphsData,
} from '@/api/workOrders/types';
import useErrorMessage from '@/hooks/useErrorMessage';
import useGeneral from '@/hooks/useGeneral';
import { usePropertyData } from '@/hooks/usePropertyData';
import useSnackbar from '@/hooks/useSnackbar';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface ScientificTreatmentHook {
  referenceNumber: number | undefined;
  isFactors: boolean;
  avmData: AvmInferenceData | AvmFactorsData | null | undefined;
  propertyType: PropertyType | undefined;
  completionData: CompletionData | undefined;
  loadingPage: boolean;
  graphs: GraphsData[];
  navigateBack: () => void;
}

const useScientificTreatment = (): ScientificTreatmentHook => {
  const [completionData, setCompletionData] = useState<
    CompletionData | undefined
  >();
  const [graphs, setGraphs] = useState<GraphsData[]>([]);
  const [loadingCompletion, setLoadingCompletion] = useState(true);
  const [manualAvmData, setManualAvmData] = useState<
    AvmInferenceData | AvmFactorsData | null | undefined
  >();

  const { osId } = useGeneral();
  const { propertyData, loadingPage } = usePropertyData({
    status: Status.REPORT,
  });
  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const navigateBack = (): void => {
    const pathParts = location.pathname.split('/');
    pathParts.pop();
    const newPath = pathParts.join('/');
    navigate(newPath);
  };

  const getGraphsCallback = useCallback(async (): Promise<void> => {
    try {
      const response = await getReportGraphs(osId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data)
        throw new Error(
          'Não foi possível carregar os gráficos, tente novamente.'
        );

      setGraphs(response.data);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      navigateBack();
    } finally {
      setLoadingCompletion(false);
    }
  }, []);

  const getManualCalculationData = async (): Promise<void> => {
    try {
      setLoadingCompletion(true);
      const response = await manualCalcultionAPI.getScientificTreatment(osId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setManualAvmData(response.data.avm_report);
      setCompletionData(response.data.completion);
      getGraphsCallback();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      setLoadingCompletion(false);
      navigateBack();
    }
  };

  useEffect(() => {
    if (!propertyData) return;
    if (propertyData.avm_status !== null) {
      navigateBack();
    } else {
      getManualCalculationData();
    }
  }, [propertyData]);

  return {
    referenceNumber: propertyData?.reference_number,
    isFactors:
      propertyData?.calculation_strategy === CalculationStrategy.FACTORS,
    avmData: manualAvmData,
    propertyType: propertyData?.real_estate_kind,
    completionData,
    loadingPage: loadingPage || loadingCompletion,
    graphs,
    navigateBack,
  };
};

export default useScientificTreatment;
